// Every date and time the app shows is Pakistan Standard Time, whatever the
// phone's own clock or region says. Pakistan has no daylight saving, so the
// offset is a constant +05:00.
export const TIME_ZONE = "Asia/Karachi";
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
  timeZone: TIME_ZONE,
};

// The Pakistan calendar date of an instant, as YYYY-MM-DD.
export function pkDayKey(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

// Client request (2026-10-02): show every PD Day as 1:00 PM – 2:00 PM for
// now, whatever its scheduled window. Display only — access still follows
// the real start/end. Set to null to show scheduled times again.
const DISPLAY_SESSION_HOURS: { start: number; end: number } | null = { start: 13, end: 14 };

function displayWindow(from: Date, to: Date): [Date, Date] {
  if (!DISPLAY_SESSION_HOURS) return [from, to];
  const at = (hour: number) =>
    new Date(`${pkDayKey(from)}T${String(hour).padStart(2, "0")}:00:00+05:00`);
  return [at(DISPLAY_SESSION_HOURS.start), at(DISPLAY_SESSION_HOURS.end)];
}

export function formatDateAndTime(iso: string, locale: string, todayLabel: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const isToday = pkDayKey(date) === pkDayKey(new Date());
  const day = isToday
    ? todayLabel
    : date.toLocaleDateString(locale, { day: "numeric", month: "short", timeZone: TIME_ZONE });
  return `${day}, ${date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", timeZone: TIME_ZONE })}`;
}

function dayLabel(date: Date, locale: string, todayLabel: string): string {
  return pkDayKey(date) === pkDayKey(new Date())
    ? todayLabel
    : date.toLocaleDateString(locale, { day: "numeric", month: "short", timeZone: TIME_ZONE });
}

// Splits a session into a day line and a time line: "Today" / "9:00 AM – 5:00 PM".
// A session that ends on a later day names that day in the time line too, so
// an overnight range is not read as ending the same day.
export function formatDateAndTimeParts(
  start: string,
  end: string,
  locale: string,
  todayLabel: string
): { day: string; time: string } {
  const parsedFrom = new Date(start);
  const parsedTo = new Date(end);
  if (Number.isNaN(parsedFrom.getTime()) || Number.isNaN(parsedTo.getTime())) {
    return { day: "", time: "" };
  }
  const [from, to] = displayWindow(parsedFrom, parsedTo);
  const endTime = to.toLocaleTimeString(locale, TIME_FORMAT);
  const sameDay = pkDayKey(from) === pkDayKey(to);
  return {
    day: dayLabel(from, locale, todayLabel),
    time: `${from.toLocaleTimeString(locale, TIME_FORMAT)} – ${
      sameDay ? endTime : `${dayLabel(to, locale, todayLabel)}, ${endTime}`
    }`,
  };
}

export function formatTimeRange(start: string, end: string, locale: string): string {
  const parsedFrom = new Date(start);
  const parsedTo = new Date(end);
  if (Number.isNaN(parsedFrom.getTime()) || Number.isNaN(parsedTo.getTime())) return "";
  const [from, to] = displayWindow(parsedFrom, parsedTo);
  return `${from.toLocaleTimeString(locale, TIME_FORMAT)} – ${to.toLocaleTimeString(locale, TIME_FORMAT)}`;
}

// Initials stay Latin-only on purpose: an Urdu name has no meaningful
// two-letter abbreviation, so those fall back to the first character.
export function initialsOf(name: string, fallback: string): string {
  const source = name.trim() || fallback.trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).slice(0, 2);
  return parts
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function formatDayDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: TIME_ZONE,
  });
}
